import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await api.get('/faqs');
      setFaqs(response.data);
    } catch (error) {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    const category = faq.category || 'general';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(faq);
    return acc;
  }, {});

  const categoryLabels = {
    general: 'General Questions',
    membership: 'Membership & Billing',
    access: 'Access & Security',
    services: 'Services & Amenities'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground">Loading FAQs...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 px-6 md:px-12 lg:px-20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
        <div className="relative z-10 container mx-auto max-w-4xl">
          <div className="flex justify-end mb-4">
            <Link to="/">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white" data-testid="back-home-btn">
                Back to Home
              </Button>
            </Link>
          </div>
          <div className="text-center">
            <HelpCircle className="w-16 h-16 text-primary mx-auto mb-6" />
            <h1 className="text-5xl md:text-6xl font-heading font-black uppercase tracking-tight mb-6" data-testid="faq-title">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Find answers to common questions about Wholesome Fitness Center
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="container mx-auto max-w-4xl">
          {faqs.length === 0 ? (
            <Card className="bg-card/50 border-white/10">
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">No FAQs available at the moment.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
                <div key={category}>
                  <h2 className="text-2xl font-heading font-bold uppercase mb-4 text-primary">
                    {categoryLabels[category] || category}
                  </h2>
                  <div className="space-y-3">
                    {categoryFaqs.map((faq, index) => {
                      const globalIndex = `${category}-${index}`;
                      const isOpen = openIndex === globalIndex;
                      
                      return (
                        <Card 
                          key={faq.id} 
                          className={`bg-card/50 border-white/10 cursor-pointer transition-all ${isOpen ? 'border-primary/50' : 'hover:border-white/20'}`}
                          onClick={() => toggleFAQ(globalIndex)}
                          data-testid={`faq-item-${faq.id}`}
                        >
                          <CardContent className="pt-4 pb-4">
                            <div className="flex justify-between items-center">
                              <h3 className="font-semibold text-lg pr-4">{faq.question}</h3>
                              {isOpen ? (
                                <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                              )}
                            </div>
                            {isOpen && (
                              <div className="mt-4 pt-4 border-t border-white/10">
                                <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 px-6 md:px-12 lg:px-20 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-heading font-black uppercase mb-4">Still Have Questions?</h2>
          <p className="text-muted-foreground mb-6">
            Our team is here to help. Reach out to us anytime!
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-primary hover:bg-primary/90">
                Contact Us
              </Button>
            </Link>
            <a href="tel:+2348142313787">
              <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary hover:text-black">
                Call: +234 814 231 3787
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
